import type { AABB, OBB, Ring, Vec2 } from '../../geo/types';
import { obbCorners } from '../../geo/polygon';

/**
 * The ground as square cells `size` metres wide from `origin`, `cols` by `rows`, numbered row by
 * row; a cell stands for the point at its centre.
 */
export interface ClaimGrid {
  readonly origin: Vec2;
  readonly size: number;
  readonly cols: number;
  readonly rows: number;
}

/* The origin sits on the cell size, so the same ground falls in the same cells whatever the box. */
export function gridOver(box: AABB, size: number): ClaimGrid {
  const origin: Vec2 = [Math.floor(box.minX / size) * size, Math.floor(box.minY / size) * size];
  return {
    origin,
    size,
    cols: Math.ceil((box.maxX - origin[0]) / size),
    rows: Math.ceil((box.maxY - origin[1]) / size),
  };
}

export function centreOf(grid: ClaimGrid, k: number): Vec2 {
  const col = k % grid.cols;
  const row = (k - col) / grid.cols;
  return [grid.origin[0] + (col + 0.5) * grid.size, grid.origin[1] + (row + 0.5) * grid.size];
}

/** The cell holding a point, or -1 where the point lies off the grid. */
export function cellAt(grid: ClaimGrid, p: Vec2): number {
  const col = Math.floor((p[0] - grid.origin[0]) / grid.size);
  const row = Math.floor((p[1] - grid.origin[1]) / grid.size);
  if (col < 0 || row < 0 || col >= grid.cols || row >= grid.rows) return -1;
  return row * grid.cols + col;
}

/* Where the rings cross the line through a row's centres, into `xs`, in order along it; how many. */
function crossings(rings: readonly Ring[], y: number, xs: number[]): number {
  let n = 0;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[j]!;
      const b = ring[i]!;
      if (a[1] <= y === b[1] <= y) continue;
      const x = a[0] + ((y - a[1]) / (b[1] - a[1])) * (b[0] - a[0]);
      let k = n++;
      for (; k > 0 && xs[k - 1]! > x; k--) xs[k] = xs[k - 1]!;
      xs[k] = x;
    }
  }
  return n;
}

/**
 * Every run of cells along a row whose centres lie inside the rings, counted even-odd so an
 * outline's holes stay out: `visit` gets the numbers of the run's first and last cells. The ground
 * runs to millions of cells, so runs, not cells, are handed out.
 */
export function eachRunIn(
  grid: ClaimGrid,
  rings: readonly Ring[],
  visit: (first: number, last: number) => void
): void {
  let lo = Infinity;
  let hi = -Infinity;
  for (const ring of rings)
    for (const p of ring) {
      lo = Math.min(lo, p[1]);
      hi = Math.max(hi, p[1]);
    }
  const [ox, oy] = grid.origin;
  const r0 = Math.max(0, Math.ceil((lo - oy) / grid.size - 0.5));
  const r1 = Math.min(grid.rows - 1, Math.floor((hi - oy) / grid.size - 0.5));
  const xs: number[] = [];
  for (let row = r0; row <= r1; row++) {
    const n = crossings(rings, oy + (row + 0.5) * grid.size, xs);
    for (let i = 0; i + 1 < n; i += 2) {
      const c0 = Math.max(0, Math.ceil((xs[i]! - ox) / grid.size - 0.5));
      const c1 = Math.min(grid.cols - 1, Math.floor((xs[i + 1]! - ox) / grid.size - 0.5));
      if (c1 >= c0) visit(row * grid.cols + c0, row * grid.cols + c1);
    }
  }
}

/** Every cell whose centre lies inside the rings, counted even-odd. */
export function eachCellIn(
  grid: ClaimGrid,
  rings: readonly Ring[],
  visit: (k: number) => void
): void {
  eachRunIn(grid, rings, (first, last) => {
    for (let k = first; k <= last; k++) visit(k);
  });
}

export function eachCellInObb(grid: ClaimGrid, obb: OBB, visit: (k: number) => void): void {
  eachCellIn(grid, [obbCorners(obb)], visit);
}
